import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../utils/supabase';
import Icon from '../AppIcon';

/**
 * TournamentBracket Component
 * Displays tournament bracket visualization for single/double elimination, Swiss, and round robin formats
 *
 * Usage:
 * <TournamentBracket tournamentId="tournament_uuid" />
 */
export default function TournamentBracket({ tournamentId }) {
  const { user } = useAuth();
  const [tournament, setTournament] = useState(null);
  const [matches, setMatches] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (tournamentId) {
      loadTournamentData();
    }
  }, [tournamentId]);

  const loadTournamentData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get tournament details
      const { data: tournamentData, error: tournamentError } = await supabase
        .from('tournaments')
        .select('*')
        .eq('id', tournamentId)
        .single();

      if (tournamentError) throw tournamentError;
      setTournament(tournamentData);

      // Get all matches
      const { data: matchesData, error: matchesError } = await supabase
        .from('tournament_matches')
        .select('*')
        .eq('tournament_id', tournamentId)
        .order('round', { ascending: true })
        .order('match_number', { ascending: true });

      if (matchesError) throw matchesError;

      // Get all participants
      const { data: participantsData, error: participantsError } = await supabase
        .from('tournament_participants')
        .select(`
          id,
          user_id,
          status,
          eliminated_at,
          placement,
          user:user_id (
            id,
            username,
            avatar_url
          )
        `)
        .eq('tournament_id', tournamentId);

      if (participantsError) throw participantsError;

      setMatches(matchesData || []);
      setParticipants(participantsData || []);

    } catch (err) {
      console.error('Error loading tournament data:', err);
      setError('Failed to load tournament bracket');
    } finally {
      setLoading(false);
    }
  };

  const getParticipantById = (userId) => {
    return participants.find(p => p.user_id === userId);
  };

  const getMatchStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 border-green-500';
      case 'in_progress':
        return 'bg-yellow-100 border-yellow-500';
      case 'pending':
        return 'bg-gray-100 border-gray-300';
      default:
        return 'bg-gray-100 border-gray-300';
    }
  };

  const renderSingleEliminationBracket = () => {
    // Group matches by round
    const rounds = matches.reduce((acc, match) => {
      if (!acc[match.round]) acc[match.round] = [];
      acc[match.round].push(match);
      return acc;
    }, {});

    const roundKeys = Object.keys(rounds).sort((a, b) => Number(a) - Number(b));
    const maxRound = Math.max(...roundKeys.map(Number));

    return (
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-8 min-w-fit">
          {roundKeys.map(round => (
            <div key={round} className="flex flex-col gap-4 min-w-[280px]">
              {/* Round header */}
              <div className="text-center mb-4">
                <h4 className="font-bold text-foreground">
                  {Number(round) === maxRound ? 'Final' :
                   Number(round) === maxRound - 1 ? 'Semi-Finals' :
                   Number(round) === maxRound - 2 ? 'Quarter-Finals' :
                   `Round ${round}`}
                </h4>
                <p className="text-xs text-muted-foreground">
                  {rounds[round].length} {rounds[round].length === 1 ? 'match' : 'matches'}
                </p>
              </div>

              {/* Matches */}
              {rounds[round].map(match => (
                <div
                  key={match.id}
                  className={`border-2 rounded-lg p-4 ${getMatchStatusColor(match.status)}`}
                >
                  {/* Match number */}
                  <div className="text-xs text-muted-foreground mb-2 flex items-center justify-between">
                    <span>Match {match.match_number}</span>
                    {match.status === 'in_progress' && (
                      <span className="flex items-center gap-1 text-yellow-600">
                        <Icon name="Clock" size={12} />
                        Live
                      </span>
                    )}
                  </div>

                  {/* Player 1 */}
                  <div className={`flex items-center gap-2 p-2 rounded mb-2 ${
                    match.winner_id === match.player1_id ? 'bg-green-200 font-bold' : 'bg-white'
                  }`}>
                    {match.player1_id ? (
                      <>
                        <div className="w-6 h-6 bg-muted rounded-full flex items-center justify-center flex-shrink-0">
                          {getParticipantById(match.player1_id)?.user?.avatar_url ? (
                            <img
                              src={getParticipantById(match.player1_id).user.avatar_url}
                              alt=""
                              className="w-6 h-6 rounded-full object-cover"
                            />
                          ) : (
                            <Icon name="User" size={12} />
                          )}
                        </div>
                        <span className="flex-1 text-sm truncate">
                          {getParticipantById(match.player1_id)?.user?.username || 'Unknown'}
                        </span>
                        {match.player1_score !== null && (
                          <span className="text-sm font-semibold">{match.player1_score}</span>
                        )}
                      </>
                    ) : (
                      <span className="text-sm text-muted-foreground italic">TBD</span>
                    )}
                  </div>

                  {/* VS */}
                  <div className="text-center text-xs text-muted-foreground my-1">VS</div>

                  {/* Player 2 */}
                  <div className={`flex items-center gap-2 p-2 rounded ${
                    match.winner_id === match.player2_id ? 'bg-green-200 font-bold' : 'bg-white'
                  }`}>
                    {match.player2_id ? (
                      <>
                        <div className="w-6 h-6 bg-muted rounded-full flex items-center justify-center flex-shrink-0">
                          {getParticipantById(match.player2_id)?.user?.avatar_url ? (
                            <img
                              src={getParticipantById(match.player2_id).user.avatar_url}
                              alt=""
                              className="w-6 h-6 rounded-full object-cover"
                            />
                          ) : (
                            <Icon name="User" size={12} />
                          )}
                        </div>
                        <span className="flex-1 text-sm truncate">
                          {getParticipantById(match.player2_id)?.user?.username || 'Unknown'}
                        </span>
                        {match.player2_score !== null && (
                          <span className="text-sm font-semibold">{match.player2_score}</span>
                        )}
                      </>
                    ) : (
                      <span className="text-sm text-muted-foreground italic">TBD</span>
                    )}
                  </div>

                  {/* Scheduled time */}
                  {match.scheduled_time && match.status === 'pending' && (
                    <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1">
                      <Icon name="Calendar" size={12} />
                      {new Date(match.scheduled_time).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit'
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderSwissBracket = () => {
    // Group matches by round
    const rounds = matches.reduce((acc, match) => {
      if (!acc[match.round]) acc[match.round] = [];
      acc[match.round].push(match);
      return acc;
    }, {});

    const roundKeys = Object.keys(rounds).sort((a, b) => Number(a) - Number(b));

    // Calculate standings based on completed matches
    const standings = participants.map(participant => {
      const userMatches = matches.filter(m =>
        (m.player1_id === participant.user_id || m.player2_id === participant.user_id) &&
        m.status === 'completed'
      );

      const wins = userMatches.filter(m => m.winner_id === participant.user_id).length;
      const losses = userMatches.length - wins;

      return {
        participant,
        wins,
        losses,
        matchesPlayed: userMatches.length
      };
    }).sort((a, b) => b.wins - a.wins);

    return (
      <div className="space-y-6">
        {/* Standings */}
        <div className="bg-card border border-border rounded-lg p-4">
          <h4 className="font-bold text-foreground mb-4">Standings</h4>
          <div className="space-y-2">
            {standings.map((entry, index) => (
              <div
                key={entry.participant.id}
                className="flex items-center gap-3 p-2 rounded bg-muted/30"
              >
                <span className="text-sm font-semibold text-muted-foreground w-8">#{index + 1}</span>
                <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center flex-shrink-0">
                  {entry.participant.user?.avatar_url ? (
                    <img
                      src={entry.participant.user.avatar_url}
                      alt=""
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <Icon name="User" size={16} />
                  )}
                </div>
                <span className="flex-1 text-sm font-medium text-foreground">
                  {entry.participant.user?.username || 'Unknown'}
                </span>
                <div className="text-sm">
                  <span className="text-green-600 font-semibold">{entry.wins}W</span>
                  {' - '}
                  <span className="text-red-600 font-semibold">{entry.losses}L</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Rounds */}
        <div className="space-y-6">
          {roundKeys.map(round => (
            <div key={round} className="bg-card border border-border rounded-lg p-4">
              <h4 className="font-bold text-foreground mb-4">Round {round}</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {rounds[round].map(match => (
                  <div
                    key={match.id}
                    className={`border-2 rounded-lg p-3 ${getMatchStatusColor(match.status)}`}
                  >
                    {/* Match number */}
                    <div className="text-xs text-muted-foreground mb-2">Match {match.match_number}</div>

                    {/* Players */}
                    <div className="space-y-2">
                      <div className={`flex items-center gap-2 p-2 rounded ${
                        match.winner_id === match.player1_id ? 'bg-green-200 font-bold' : 'bg-white'
                      }`}>
                        {match.player1_id && (
                          <>
                            <div className="w-6 h-6 bg-muted rounded-full flex items-center justify-center">
                              <Icon name="User" size={12} />
                            </div>
                            <span className="flex-1 text-sm">
                              {getParticipantById(match.player1_id)?.user?.username || 'Unknown'}
                            </span>
                            {match.player1_score !== null && (
                              <span className="text-sm font-semibold">{match.player1_score}</span>
                            )}
                          </>
                        )}
                      </div>

                      <div className={`flex items-center gap-2 p-2 rounded ${
                        match.winner_id === match.player2_id ? 'bg-green-200 font-bold' : 'bg-white'
                      }`}>
                        {match.player2_id && (
                          <>
                            <div className="w-6 h-6 bg-muted rounded-full flex items-center justify-center">
                              <Icon name="User" size={12} />
                            </div>
                            <span className="flex-1 text-sm">
                              {getParticipantById(match.player2_id)?.user?.username || 'Unknown'}
                            </span>
                            {match.player2_score !== null && (
                              <span className="text-sm font-semibold">{match.player2_score}</span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderRoundRobinBracket = () => {
    // Create matrix of all possible matchups
    const standings = participants.map(participant => {
      const userMatches = matches.filter(m =>
        (m.player1_id === participant.user_id || m.player2_id === participant.user_id) &&
        m.status === 'completed'
      );

      const wins = userMatches.filter(m => m.winner_id === participant.user_id).length;
      const losses = userMatches.length - wins;

      return {
        participant,
        wins,
        losses,
        matchesPlayed: userMatches.length
      };
    }).sort((a, b) => b.wins - a.wins);

    return (
      <div className="space-y-6">
        {/* Standings */}
        <div className="bg-card border border-border rounded-lg p-4">
          <h4 className="font-bold text-foreground mb-4">Standings</h4>
          <div className="space-y-2">
            {standings.map((entry, index) => (
              <div
                key={entry.participant.id}
                className="flex items-center gap-3 p-2 rounded bg-muted/30"
              >
                <span className="text-sm font-semibold text-muted-foreground w-8">#{index + 1}</span>
                <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center flex-shrink-0">
                  {entry.participant.user?.avatar_url ? (
                    <img
                      src={entry.participant.user.avatar_url}
                      alt=""
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <Icon name="User" size={16} />
                  )}
                </div>
                <span className="flex-1 text-sm font-medium text-foreground">
                  {entry.participant.user?.username || 'Unknown'}
                </span>
                <div className="text-sm">
                  <span className="text-green-600 font-semibold">{entry.wins}W</span>
                  {' - '}
                  <span className="text-red-600 font-semibold">{entry.losses}L</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* All matches */}
        <div className="bg-card border border-border rounded-lg p-4">
          <h4 className="font-bold text-foreground mb-4">All Matches</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {matches.map(match => (
              <div
                key={match.id}
                className={`border-2 rounded-lg p-3 ${getMatchStatusColor(match.status)}`}
              >
                {/* Match info */}
                <div className="text-xs text-muted-foreground mb-2 flex items-center justify-between">
                  <span>Match {match.match_number}</span>
                  {match.status === 'in_progress' && (
                    <span className="text-yellow-600">Live</span>
                  )}
                </div>

                {/* Players */}
                <div className="space-y-2">
                  <div className={`flex items-center gap-2 p-2 rounded ${
                    match.winner_id === match.player1_id ? 'bg-green-200 font-bold' : 'bg-white'
                  }`}>
                    <div className="w-6 h-6 bg-muted rounded-full flex items-center justify-center">
                      <Icon name="User" size={12} />
                    </div>
                    <span className="flex-1 text-sm">
                      {getParticipantById(match.player1_id)?.user?.username || 'Unknown'}
                    </span>
                    {match.player1_score !== null && (
                      <span className="text-sm font-semibold">{match.player1_score}</span>
                    )}
                  </div>

                  <div className={`flex items-center gap-2 p-2 rounded ${
                    match.winner_id === match.player2_id ? 'bg-green-200 font-bold' : 'bg-white'
                  }`}>
                    <div className="w-6 h-6 bg-muted rounded-full flex items-center justify-center">
                      <Icon name="User" size={12} />
                    </div>
                    <span className="flex-1 text-sm">
                      {getParticipantById(match.player2_id)?.user?.username || 'Unknown'}
                    </span>
                    {match.player2_score !== null && (
                      <span className="text-sm font-semibold">{match.player2_score}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
            <p className="text-sm text-muted-foreground">Loading bracket...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto mb-4" />
            <p className="text-red-500">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tournament header */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-2">{tournament.name}</h2>
            {tournament.description && (
              <p className="text-sm text-muted-foreground">{tournament.description}</p>
            )}
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-semibold text-white ${
            tournament.status === 'active' ? 'bg-green-500' :
            tournament.status === 'pending' ? 'bg-yellow-500' :
            'bg-gray-500'
          }`}>
            {tournament.status}
          </span>
        </div>

        {/* Tournament info */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Format</p>
            <p className="text-sm font-semibold text-foreground">
              {tournament.bracket_type === 'single_elimination' ? 'Single Elimination' :
               tournament.bracket_type === 'double_elimination' ? 'Double Elimination' :
               tournament.bracket_type === 'swiss' ? 'Swiss System' :
               tournament.bracket_type === 'round_robin' ? 'Round Robin' :
               tournament.bracket_type}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Participants</p>
            <p className="text-sm font-semibold text-foreground">
              {participants.length}{tournament.max_participants ? `/${tournament.max_participants}` : ''}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Start Time</p>
            <p className="text-sm font-semibold text-foreground">
              {new Date(tournament.start_time).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit'
              })}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Prize Pool</p>
            <p className="text-sm font-semibold text-foreground">
              {tournament.prize_pool?.coins ? `${tournament.prize_pool.coins} coins` : 'Glory'}
            </p>
          </div>
        </div>
      </div>

      {/* Bracket visualization */}
      <div className="bg-card border border-border rounded-lg p-6">
        {matches.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Trophy" size={64} className="text-muted-foreground mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-semibold text-foreground mb-2">Bracket Not Generated Yet</h3>
            <p className="text-sm text-muted-foreground">
              The tournament bracket will be generated once the tournament starts.
            </p>
          </div>
        ) : (
          <>
            {tournament.bracket_type === 'single_elimination' && renderSingleEliminationBracket()}
            {tournament.bracket_type === 'double_elimination' && renderSingleEliminationBracket()}
            {tournament.bracket_type === 'swiss' && renderSwissBracket()}
            {tournament.bracket_type === 'round_robin' && renderRoundRobinBracket()}
          </>
        )}
      </div>
    </div>
  );
}
